import { Platform } from 'react-native';

/**
 * Platform detection utilities for distinguishing between
 * native apps, desktop web, and mobile web at runtime.
 */

/**
 * Returns true if running in a web browser (any browser).
 */
export function isWebPlatform(): boolean {
  return Platform.OS === 'web';
}

/**
 * Returns true if running as a native app (iOS or Android).
 */
export function isNativePlatform(): boolean {
  return Platform.OS === 'ios' || Platform.OS === 'android';
}

/**
 * Checks if the user agent indicates a mobile device.
 * Detects iPhone, iPad, iPod, Android, and other mobile browsers.
 * Also catches iPadOS 13+ which spoofs a Mac desktop UA but has touch support.
 */
function isMobileUserAgent(): boolean {
  if (typeof navigator === 'undefined' || !navigator.userAgent) {
    return false;
  }
  // iPadOS 13+ sends "Macintosh" UA but exposes maxTouchPoints > 1 (iPads report 5)
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) {
    return true;
  }
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(ua);
}

/**
 * Returns true if running on a desktop web browser (Mac Safari, Chrome desktop, etc.).
 * False on native apps and mobile web browsers.
 */
export function isDesktopWeb(): boolean {
  if (Platform.OS !== 'web') return false;
  return !isMobileUserAgent();
}

/**
 * Returns true if running on a mobile web browser (iOS Safari, Chrome mobile, etc.).
 * False on native apps and desktop web browsers.
 */
export function isMobileWeb(): boolean {
  if (Platform.OS !== 'web') return false;
  return isMobileUserAgent();
}

/**
 * True when the primary pointer can hover — a mouse or trackpad.
 *
 * Prefer this over user-agent sniffing when the question is "can this person
 * drag / will they see hover-revealed controls", because it reports the input
 * device rather than the brand. It is also the one signal here that Chrome's
 * device emulation and real hardware agree on: emulation reports
 * `maxTouchPoints: 1` where real iPadOS reports 5, but both answer
 * `(hover: hover)` correctly.
 *
 * Returns false when there is no `window` (SSR, jest). False is the safe
 * default: it shows tap-based affordances, which are accurate everywhere,
 * whereas defaulting true would tell a touch user to drag something that
 * HTML5 drag will never let them drag.
 *
 * On a hybrid device (touchscreen laptop) `(hover: hover)` reports the PRIMARY
 * pointer, so a trackpad laptop that also has a touchscreen correctly reads as
 * hover-capable.
 */
export function canHover(): boolean {
  if (Platform.OS !== 'web') return false;
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return !!window.matchMedia('(hover: hover)').matches;
}
